import { MESSAGE_TYPES } from './MessageTypes';
import { logger } from '@/utils/logger';

/**
 * Iframe消息处理器
 * 负责处理来自iframe的各种消息
 */
export class IframeMessageHandler {
  constructor(
    eventDispatcher,
    stateManager,
    chatStateStore,
    messageStatusStore, // 保留参数以保持接口兼容性，但不再使用
    callbacks = {},
    scrollManager = null
  ) {
    this.eventDispatcher = eventDispatcher;
    this.stateManager = stateManager;
    this.chatStateStore = chatStateStore;
    this.messageStatusStore = null; // 已删除消息状态功能
    this.callbacks = callbacks;
    this.scrollManager = scrollManager;
  }

  /**
   * 处理来自iframe的消息
   * @param {MessageEvent} event - 消息事件
   * @param {HTMLIFrameElement} iframe - iframe元素
   */
  handleMessage(event, iframe) {
    // 只处理来自指定iframe的消息
    if (!iframe || event.source !== iframe.contentWindow) {
      return;
    }

    // 当收到任何来自iframe的消息时，触发iframe就绪事件
    if (!this.stateManager.isIframeReady) {
      this.stateManager.setIframeReady(true);
      this.eventDispatcher.dispatchIframeReady(iframe, event.data?.type);
    }

    const { type, data } = event.data;

    switch (type) {
      case MESSAGE_TYPES.CHAT_READY:
        this._handleChatReady(data);
        break;

      case MESSAGE_TYPES.SWITCH_CONVERSATION_SUCCESS:
        this._handleSwitchConversationSuccess(data);
        break;

      case MESSAGE_TYPES.SWITCH_CONVERSATION_FAILED:
        this._handleSwitchConversationFailed(data);
        break;

      case MESSAGE_TYPES.NOTIFY_COMPLETE:
        this._handleNotifyComplete(data);
        break;

      default:
        break;
    }
  }

  /**
   * 处理聊天就绪消息
   * @param {Object} data - 消息数据
   */
  _handleChatReady(data) {
    if (data.ready) {
      if (data.type === MESSAGE_TYPES.MOUNTED) {
        // 这里可以触发发送初始化消息的回调
        if (this.onMountedCallback) {
          this.onMountedCallback();
        }
      } else if (data.type === MESSAGE_TYPES.INIT_CHAT) {
        // 初始化完成
        this.stateManager.setInitialized(true);
        this.chatStateStore.setError(null);
        this.chatStateStore.setUpdating(false);
        // 静默处理初始化完成，不显示成功消息
      }
    } else {
      logger.error('[IframeMessageHandler] iframe未就绪:', data.error);
      this.chatStateStore.setError(data.error || 'iframe未就绪');
      this.chatStateStore.setUpdating(false);
    }
  }

  /**
   * 处理切换会话成功消息
   * @param {Object} data - 消息数据
   */
  _handleSwitchConversationSuccess(data) {
    logger.debug('会话切换：收到切换会话成功通知', {
      conversationId: data.conversationId,
      message: data.message,
    });

    // 设置会话切换完成状态
    this.chatStateStore.setSwitchingConversation(false);
    this.chatStateStore.setUpdating(false);
    this.chatStateStore.setConnecting(false);
    this.chatStateStore.setPreparing(false);
    this.chatStateStore.setInitializing(false);

    // 清除所有通知消息

    // 通知其他组件切换会话成功
    this.eventDispatcher.dispatchConversationSwitched(data.conversationId, data.message);

    // 调用回调函数（但不包含滚动操作）
    if (this.callbacks.onSwitchConversationSuccess) {
      this.callbacks.onSwitchConversationSuccess(data);
    } else {
      logger.warn('[IframeMessageHandler] 未找到切换会话成功回调函数');
    }
    // 在消息处理完成后，执行滚动到底部操作
    this._executeScrollToBottom(data.conversationId);
  }

  /**
   * 处理切换会话失败消息
   * @param {Object} data - 消息数据
   */
  _handleSwitchConversationFailed(data) {
    logger.error('会话切换：收到切换会话失败通知', {
      conversationId: data.conversationId,
      error: data.error,
      message: data.message,
    });

    // 设置会话切换失败状态
    this.chatStateStore.setSwitchingConversation(false);
    this.chatStateStore.setUpdating(false);
    this.chatStateStore.setConnecting(false);
    this.chatStateStore.setPreparing(false);
    this.chatStateStore.setInitializing(false);

    // 显示错误信息
    this.chatStateStore.setError(data.error || '切换会话失败');
    this.chatStateStore.showError(data.message || '切换会话失败，请重试');

    // 通知其他组件切换会话失败
    this.eventDispatcher.dispatchConversationSwitchError(
      data.conversationId,
      data.error,
      data.message,
      'error'
    );

    logger.error('会话切换：切换会话失败，状态已重置', {
      conversationId: data.conversationId,
      error: data.error,
      message: data.message,
    });
  }

  /**
   * 处理通知完成消息
   * @param {Object} data - 消息数据
   */
  _handleNotifyComplete() {
    // logger.info('[IframeMessageHandler] 收到通知完成:', data);
  }

  /**
   * 执行滚动到底部操作
   * @param {string} conversationId - 会话ID
   */
  _executeScrollToBottom(conversationId) {
    // 这里需要访问滚动管理器，我们需要通过构造函数传入
    if (this.scrollManager) {
      this.scrollManager.autoScrollToBottomAfterSwitch(conversationId);
    } else {
      logger.warn('[IframeMessageHandler] 滚动管理器未初始化，无法执行滚动操作');
    }
  }
}
